import { WebVTTParser } from "webvtt-parser";

export interface ThumbnailCue {
  endTime: number;
  height: number;
  startTime: number;
  width: number;
  x: number;
  y: number;
}

interface ParsedThumbnailVtt {
  cues: ThumbnailCue[];
  imageUrl: string | null;
}

/**
 * Parses a thumbnail VTT file and extracts thumbnail information
 * @param vttContent - The content of the VTT file
 * @returns Parsed thumbnail data
 */
export const parseThumbnailVtt = (vttContent: string): ParsedThumbnailVtt => {
  const parser = new WebVTTParser();
  const result = parser.parse(vttContent);

  if (result.errors.length) {
    console.warn("VTT parsing errors:", result.errors);
  }

  return result.cues.reduce<ParsedThumbnailVtt>(
    (acc, cue) => {
      const thumbnailData = parseThumbnailText(cue.text);
      if (!thumbnailData) return acc;

      const { imageUrl, ...cueData } = thumbnailData;

      return {
        cues: [
          ...acc.cues,
          {
            startTime: cue.startTime,
            endTime: cue.endTime,
            ...cueData,
          },
        ],
        imageUrl: acc.imageUrl ?? imageUrl.split("#")[0],
      };
    },
    { cues: [], imageUrl: null },
  );
};

/**
 * Parses the thumbnail text from a VTT cue
 * Example: "https://example.com/thumbnails.jpg#xywh=0,0,160,90"
 * @param text - The text of the VTT cue
 * @returns The parsed thumbnail data
 */
const parseThumbnailText = (text: string) => {
  // eslint-disable-next-line sonarjs/slow-regex -- Disabled for plugin version update. Fix when possible.
  const cleanText = text.replace(/<[^>]*>/g, "").trim();
  // eslint-disable-next-line sonarjs/prefer-regexp-exec -- Disabled for plugin version update. Fix when possible.
  const match = cleanText.match(/^(.+)#xywh=(\d+),(\d+),(\d+),(\d+)$/);

  if (!match) return null;

  const [, imageUrl, ...dimensions] = match;
  const [x, y, width, height] = dimensions.map(d => parseInt(d, 10));

  return { imageUrl, x, y, width, height };
};

/**
 * Extracts a thumbnail from the thumbnail sprite for a specific time
 * @param time - Timestamp
 * @param cues - The thumbnail cues
 * @param thumbnailUrl - The URL of the thumbnail image
 * @returns A promise that resolves to a data URL of the extracted thumbnail, or null if not found
 */
export const getChapterThumbnailForTime = async ({
  cues,
  time,
  thumbnailUrl,
}: {
  cues: ThumbnailCue[];
  time: number;
  thumbnailUrl: string;
}): Promise<string | null> => {
  const cue = getThumbnailCueForTime(cues, time);

  if (!cue) return null;

  try {
    return await extractThumbnailFromSprite(cue, thumbnailUrl);
  } catch (error) {
    console.error(`Failed to extract thumbnail for chapter at ${time}:`, error);
    return null;
  }
};

/**
 * Gets the thumbnail for a specific time
 * @param cues - The thumbnail cues
 * @param time - Timestamp
 * @returns The thumbnail cue for the given time, or null if not found
 */
const getThumbnailCueForTime = (cues: ThumbnailCue[], time: number) =>
  cues.find(cue => time >= cue.startTime && time < cue.endTime) ?? null;

/**
 * Creates a thumbnail from the thumbnail sprite and returns it as a data URL
 * @param cue - The thumbnail cue containing position info
 * @param thumbnailUrl - The URL of the thumbnail image
 * @returns A promise that resolves to a data URL of the extracted thumbnail
 */
const extractThumbnailFromSprite = (
  cue: ThumbnailCue,
  thumbnailUrl: string,
): Promise<string> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = cue.width;
        canvas.height = cue.height;
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          throw new Error("Failed to get canvas context");
        }

        // Draw the specific region from the sprite
        ctx.drawImage(
          img,
          cue.x,
          cue.y,
          cue.width,
          cue.height,
          0,
          0,
          cue.width,
          cue.height,
        );

        resolve(canvas.toDataURL());
      } catch (error) {
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    };

    img.onerror = () => {
      reject(new Error(`Failed to load sprite image: ${thumbnailUrl}`));
    };

    img.src = thumbnailUrl;
  });
